/**
 * Bespoke <table> for the Alerts index — see Table.module.scss's header
 * comment for why this isn't theme/untitled/Table. Mirrors
 * CalendarsPage/Table.jsx's structure exactly (same column shape:
 * Name(+integration)/Template/Last edited/Status/Actions).
 */
import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import Icon from "../../theme/untitled/icons/Icon";
import TableRow from "../../component/WidgetIndex/TableRow";
import { getEditUrl, TEMPLATE_LABELS } from "./constants";
import styles from "./Table.module.scss";

const COLUMNS = [
  { key: "name", label: "Widget name", sortable: true },
  { key: "template", label: "Template", sortable: false, mobileHidden: true },
  {
    key: "lastEdited",
    label: "Last edited",
    sortable: true,
    align: "right",
    mobileHidden: true,
  },
  { key: "status", label: "Status", sortable: false },
];

const COLUMN_COL_CLASS = {
  template: "colTemplate",
  lastEdited: "colLastEdited",
  status: "statusCol",
};

// Same skeleton pattern as theme/untitled/Table.jsx (4 rows, alternating
// 60/40 widths) — alerts aren't pre-fetched at session/org-switch time the
// way calendars are (compare reducers/alerts.js to reducers/calendars.js's
// USER_LOGIN_FULFILLED-driven collection hydration), so AlertsPage's first
// fetch is genuinely visible rather than usually-already-resolved.
const SKELETON_WIDTH_CLASSES = ["skeletonBar60", "skeletonBar40", "skeletonBar60", "skeletonBar40"];

const AlertsTable = ({
  rows,
  sortBy,
  onSortChange,
  renderStatus,
  renderMenu,
  rowExtra,
  isRowPublishing,
  loading = false,
}) => {
  const handleSort = useCallback(
    (columnKey) => {
      const column = COLUMNS.find((c) => c.key === columnKey);
      if (!column?.sortable) return;
      const direction =
        sortBy?.key === column.key && sortBy.direction === "asc" ? "desc" : "asc";
      onSortChange?.({ key: column.key, direction });
    },
    [sortBy, onSortChange]
  );

  // Stable per-column onClick references — handleSort itself is memoized,
  // but `() => handleSort(column)` inline in the map below would still be a
  // fresh closure every render. Precomputing one handler per sortable
  // column keeps each button's onClick prop referentially stable.
  const sortHandlers = useMemo(
    () =>
      Object.fromEntries(
        COLUMNS.filter((c) => c.sortable).map((c) => [c.key, () => handleSort(c.key)])
      ),
    [handleSort]
  );

  const colElements = COLUMNS.slice(1).map((column) => (
    <col key={column.key} className={styles[COLUMN_COL_CLASS[column.key]]} />
  ));

  const headerCells = COLUMNS.map((column) => {
    const sorted = sortBy?.key === column.key;
    return (
      <th
        key={column.key}
        scope="col"
        className={classNames(styles.th, {
          [styles["th--last-edited"]]: column.align === "right",
          [styles["th--sorted"]]: sorted,
          [styles.templateCell]: column.key === "template",
          [styles.lastEditedCell]: column.key === "lastEdited",
        })}
      >
        {column.sortable ? (
          <button
            type="button"
            className={styles.sortButton}
            onClick={sortHandlers[column.key]}
            aria-sort={
              sorted ? (sortBy.direction === "asc" ? "ascending" : "descending") : "none"
            }
          >
            {column.label}
            <Icon
              name="ArrowDown"
              size={16}
              className={classNames(styles.sortIcon, {
                [styles["sortIcon--asc"]]: sorted && sortBy.direction === "asc",
                [styles["sortIcon--active"]]: sorted,
              })}
            />
          </button>
        ) : (
          column.label
        )}
      </th>
    );
  });

  const skeletonRows = SKELETON_WIDTH_CLASSES.map((widthClass, i) => (
    <tr key={i}>
      <td colSpan={COLUMNS.length + 1} className={styles.td}>
        <div className={classNames(styles.skeletonBar, styles[widthClass])} />
      </td>
    </tr>
  ));

  const bodyRows = rows.map((alert) => (
    <React.Fragment key={alert.uuid}>
      <TableRow
        item={alert}
        editUrl={getEditUrl(alert)}
        templateLabel={TEMPLATE_LABELS[alert.template] || alert.template}
        disabledReason={
          alert.disabled_date ? "This widget has been disabled due to plan downgrade." : null
        }
        renderStatus={renderStatus}
        renderMenu={renderMenu}
        isPublishing={isRowPublishing?.(alert) || false}
      />
      {rowExtra?.(alert)}
    </React.Fragment>
  ));

  return (
    <table className={styles.table}>
      <colgroup>
        <col />
        {colElements}
        <col className={styles.actionsCol} />
      </colgroup>
      <thead>
        <tr>
          {headerCells}
          <th scope="col" className={classNames(styles.th, styles.thActions)}>
            <span className={styles.srOnly}>Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>{loading ? skeletonRows : bodyRows}</tbody>
    </table>
  );
};

export default AlertsTable;
